import cn from 'classnames';
import * as React from 'react';

import { ComponentType } from 'shared/entities/components/Component';
import {
  TranslationString,
  useTypedTranslation
} from 'shared/entities/localization';
import { Button } from 'shared/newComponents/buttons';
import { CloseIcon } from 'shared/newComponents/icons';
import Reference from 'shared/newComponents/Reference';
import Typography from 'shared/newComponents/Typography';
import { ButtonColor, ButtonSize } from 'shared/newEntities/components/Button';
import { ReferenceProps } from 'shared/newEntities/components/Reference';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';

import './ModalHeader.modules.scss';

type Props = {
  titleId: string;
  subTitleId: string;
  title?: TranslationString | React.ReactNode;
  subTitle?: TranslationString | string;
  titleRef?: React.RefObject<HTMLHeadingElement>;
  titleFontSize?: TypographySize.l | TypographySize.m;
  headerExtraComponent?: React.ReactNode;
  headerRightComponent?: React.ReactNode;
  headerLeftComponent?: React.ReactNode;
  reference?: ReferenceProps;
  showCloseButton: boolean;
  onClose: () => void;
};

const ModalHeader: React.FC<Props> = ({
  titleId,
  subTitleId,
  title,
  subTitle,
  titleRef,
  titleFontSize = TypographySize.l,
  headerExtraComponent,
  headerRightComponent,
  headerLeftComponent,
  reference,
  showCloseButton,
  onClose
}: Props) => {
  const { t } = useTypedTranslation('components');
  const hasLeftContent = Boolean(
    headerLeftComponent || title || reference || headerExtraComponent
  );

  // Без подзаголовка блок остаётся прямым потомком шапки, как было до subTitle
  const leftBlock = (
    <div styleName="modal-header__left">
      {headerLeftComponent && (
        <div styleName="modal-header__left-icon">{headerLeftComponent}</div>
      )}
      {title && (
        <Typography
          Element="h2"
          ref={titleRef}
          tabIndex={-1}
          id={titleId}
          // Подзаголовок читается скринридером следом за заголовком
          aria-describedby={subTitle ? subTitleId : undefined}
          fontType={TypographyType.text}
          fontSize={titleFontSize}
          fontWeight={TypographyWeight.heavy}
          styleName="modal-header__title"
        >
          {title}
        </Typography>
      )}
      {reference && <Reference {...reference} />}
      {headerExtraComponent && headerExtraComponent}
    </div>
  );

  return (
    <div styleName="modal-header">
      {subTitle ? (
        <div styleName="modal-header__main">
          {leftBlock}
          <Typography
            id={subTitleId}
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.tertiary}
            styleName="modal-header__subtitle"
          >
            {subTitle}
          </Typography>
        </div>
      ) : (
        leftBlock
      )}
      <div
        styleName={cn(
          'modal-header__right',
          !hasLeftContent && 'modal-header__right_without-margin'
        )}
      >
        {headerRightComponent && headerRightComponent}
        {showCloseButton && (
          <Button
            type={ComponentType.button}
            onClick={onClose}
            styleName="modal-header__close"
            Icon={CloseIcon}
            size={ButtonSize.m}
            color={ButtonColor.gray}
            ariaLabel={t('modal.close')}
          />
        )}
      </div>
    </div>
  );
};

export default ModalHeader;
